/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu } from 'antd';
import * as S from './SiderMenu.styles';
import { SidebarNavigationItem } from '../sidebarNavigation';

interface SiderMenuProps {
  items: SidebarNavigationItem[];
  setCollapsed: (isCollapsed: boolean) => void;
  isCollapsed: boolean;
}

const flatten = (items: SidebarNavigationItem[]): SidebarNavigationItem[] =>
  items.flatMap((it) => [it, ...(it.children ? flatten(it.children) : [])]);

const findPath = (
  items: SidebarNavigationItem[],
  pathname: string,
  acc: string[] = []
): string[] => {
  for (const it of items) {
    if (it.url === pathname) return [...acc, it.key];
    if (it.children?.length) {
      const res = findPath(it.children, pathname, [...acc, it.key]);
      if (res.length) return res;
    }
  }
  return [];
};

const SiderMenu: React.FC<SiderMenuProps> = ({
  items,
  setCollapsed,
  isCollapsed,
}) => {
  const location = useLocation();

  const activePath = useMemo(
    () => findPath(items, location.pathname),
    [items, location.pathname]
  );

  const [openKeys, setOpenKeys] = useState<string[]>(activePath.slice(0, -1));
  useEffect(() => setOpenKeys(activePath.slice(0, -1)), [activePath]);

  useEffect(() => {
    if (isCollapsed) setOpenKeys([]);
  }, [isCollapsed]);

  const rootKeys = useMemo(() => items.map((i) => i.key), [items]);

  const onOpenChange = (keys: string[]) => {
    const latest = keys.find((k) => !openKeys.includes(k));
    if (latest && rootKeys.includes(latest)) {
      setOpenKeys([latest]);
    } else {
      setOpenKeys(keys);
    }
  };

  // Recursive Render
  const renderMenu = (nodes: SidebarNavigationItem[]) =>
    nodes.map((n) =>
      n.children?.length ? (
        <Menu.SubMenu key={n.key} icon={n.icon} title={n.title}>
          {renderMenu(n.children)}
        </Menu.SubMenu>
      ) : (
        <Menu.Item key={n.key} icon={n.icon}>
          <Link to={n.url ?? ''}>{n.title}</Link>
        </Menu.Item>
      )
    );

  return (
    <S.Menu
      mode="inline"
      inlineCollapsed={false}
      selectedKeys={[activePath.at(-1) ?? '']}
      openKeys={openKeys}
      onOpenChange={onOpenChange}
      onClick={() => setCollapsed(true)}
    >
      {renderMenu(items)}
    </S.Menu>
  );
};

export default SiderMenu;
